import classNames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../hooks';
import { useScreenshot } from '../../hooks/useScreenshot';
import Button from '../Button/Button';
import { ShareModalPlatforms } from '../NativeShareModal/NativeShareEnums';
import { InstagramIcon } from '../SocialIcons/InstagramIcon';
import { SnapchatIcon } from '../SocialIcons/SnapchatIcon';
import styles from './NativeShareButton.scss';

interface NativeShareButtonProps {
  platform: ShareModalPlatforms;
  screenshotRef: React.RefObject<HTMLDivElement>;
}

export const NativeShareButton: React.FunctionComponent<NativeShareButtonProps> = ({
  platform,
  screenshotRef,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();
  const { takeScreenshot } = useScreenshot({
    quality: 1,
    pixelRatio: 2,
  });

  const takeShareScreenshot = async () => {
    const image = await takeScreenshot(screenshotRef.current!);

    image
      ? shareImage(image)
      : showToast({
          message: t('shareModal.instagram.errorWhenSharing'),
          type: 'warning',
          dismissAfter: 3000,
        });
  };

  const shareImage = async (image: Blob) => {
    const shareData = {
      files: [
        new File([image], 'JustGivingShare.jpg', {
          type: 'image/jpeg',
        }),
      ],
    } as ShareData;

    const navigator = window.navigator as any;

    if (navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        if (!error!.toString().includes('AbortError' || 'share canceled')) {
          showToast({
            message: t('shareModal.instagram.errorWhenSharing'),
            type: 'warning',
            dismissAfter: 3000,
          });
        }
      }
    } else {
      showToast({
        message: t('shareModal.instagram.unsupportedBrowser'),
        type: 'warning',
        dismissAfter: 3000,
      });
    }
  };

  return (
    <>
      <Button
        className={classNames(
          styles.shareButton,
          platform === ShareModalPlatforms.INSTAGRAM && styles.igShareButton,
          platform === ShareModalPlatforms.SNAPCHAT && styles.snapShareButton,
        )}
        onClick={() => takeShareScreenshot()}
      >
        {platform === ShareModalPlatforms.INSTAGRAM && (
          <>
            <InstagramIcon width="24px" height="24px" color="#fff" />{' '}
            {t('shareModal.instagram.share')}{' '}
          </>
        )}
        {platform === ShareModalPlatforms.SNAPCHAT && (
          <>
            <SnapchatIcon
              width="24px"
              height="24px"
              logoBgColor="#000"
              logoSnapFillColor="#000"
              logoSnapLineColor="#fff"
            />{' '}
            {t('shareModal.snapchat.share')}{' '}
          </>
        )}
      </Button>
    </>
  );
};
